import { standardRouteDefs } from '../../../app/router/paths';
import { canAccessPath } from '../../../app/router/accessControl';
import { hasAnyPermission, hasEveryPermission } from '../../auth/permissions';
import { getNavShowcaseId } from './navigationConfig';

const SHOWCASE_STEPS = Object.freeze([
  {
    moduleKey: 'workspaceShell',
    stepKey: 'shell.guide',
    route: '__start__',
    title: 'This guide stays available',
    description: 'Use the Guide button any time to replay workspace help for your current role.',
    selectors: ['[data-showcase-id="topbar-guide"]'],
  },
  {
    moduleKey: 'workspaceShell',
    stepKey: 'shell.search',
    route: '__start__',
    title: 'Search pages and actions quickly',
    description: 'Use search to jump across the workspace without hunting through every section.',
    selectors: ['[data-showcase-id="sidebar-search"]', '[data-showcase-id="topbar-command"]'],
    needsSidebar: true,
  },
  {
    moduleKey: 'workspaceShell',
    stepKey: 'shell.profile',
    route: '__start__',
    title: 'Your profile and session controls',
    description: 'Open your profile menu for account actions, sign-out, and other session controls.',
    selectors: ['[data-showcase-id="topbar-account"]'],
  },
  {
    moduleKey: 'dashboard',
    stepKey: 'dashboard.home',
    route: '/dashboard',
    title: 'Start from the business dashboard',
    description: 'The dashboard gives you the current pulse of billing, collections, and activity.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/dashboard')}"]`, '[data-showcase-id="page-header"]'],
    needsSidebar: true,
  },
  {
    moduleKey: 'dashboard',
    stepKey: 'dashboard.customize',
    route: '/dashboard',
    title: 'Adjust your own dashboard view',
    description: 'Customize lets you change your dashboard layout without changing the whole workspace for everyone.',
    selectors: ['[data-showcase-id="dashboard-customize"]'],
    permissionsAny: ['dashboard:customize'],
  },
  {
    moduleKey: 'create',
    stepKey: 'create.add',
    route: '__start__',
    title: 'Create from the Add menu',
    description: 'Use Add for the billing documents your role is allowed to create.',
    selectors: ['[data-showcase-id="topbar-add"]'],
  },
  {
    moduleKey: 'invoices',
    stepKey: 'invoices.nav',
    route: '__start__',
    title: 'Invoices',
    description: 'Invoices is where you issue bills, review history, and follow payment status.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/invoices')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'quotations',
    stepKey: 'quotations.nav',
    route: '__start__',
    title: 'Quotations',
    description: 'Use quotations to prepare customer-ready proposals before they become billing documents.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/quotations')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'estimatedInvoices',
    stepKey: 'estimatedInvoices.nav',
    route: '__start__',
    title: 'Estimates and cash memos',
    description: 'Estimated invoices help you share draft billing values before final invoicing.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/estimated-invoices')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'customers',
    stepKey: 'customers.nav',
    route: '__start__',
    title: 'Customer records',
    description: 'Maintain customer contact and billing details here so documents stay consistent.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/customers')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'products',
    stepKey: 'products.nav',
    route: '__start__',
    title: 'Product catalog',
    description: 'Keep product and pricing records ready here for document lookups and reuse.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/products')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'taxes',
    stepKey: 'taxes.nav',
    route: '__start__',
    title: 'Tax rules',
    description: 'Manage reusable tax rules centrally so billing stays consistent across documents.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/taxes')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'settings',
    stepKey: 'settings.nav',
    route: '__start__',
    title: 'Company settings',
    description: 'Settings holds the company identity and billing defaults used across the workspace.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/settings')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'tenantUsers',
    stepKey: 'tenantUsers.page',
    route: '/settings/admin',
    title: 'Tenant user access',
    description: 'Use this area to review who has access to the workspace and what role template they use.',
    selectors: ['[data-showcase-id="page-header"]'],
  },
  {
    moduleKey: 'fieldController',
    stepKey: 'fieldController.nav',
    route: '__start__',
    title: 'Field controller',
    description: 'Field controller governs which standard fields stay visible or required across forms.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/settings/fields')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'customFields',
    stepKey: 'customFields.nav',
    route: '__start__',
    title: 'Custom fields',
    description: 'Custom fields let your team capture extra business data without changing core records.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/settings/custom-fields')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'documentLayouts',
    stepKey: 'documentLayouts.nav',
    route: '__start__',
    title: 'Document templates',
    description: 'Document templates control the reusable PDF layouts used for customer-facing outputs.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/document-templates')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'documentFormLayouts',
    stepKey: 'documentFormLayouts.nav',
    route: '__start__',
    title: 'Form layouts',
    description: 'Form layouts help you decide how billing forms are arranged for faster entry.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/document-form-layouts')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'activityHistory',
    stepKey: 'activityHistory.nav',
    route: '__start__',
    title: 'Activity history',
    description: 'Activity history lets you review important changes and the timeline behind them.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/activity-history')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'campaigns',
    stepKey: 'campaigns.nav',
    route: '__start__',
    title: 'Campaigns',
    description: 'Campaigns helps your team prepare, preview, and send customer-facing announcements.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/campaigns')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'ledger',
    stepKey: 'ledger.nav',
    route: '__start__',
    title: 'Sales book',
    description: 'Use the sales book to review ledger-style billing activity and reporting context.',
    selectors: [`[data-showcase-id="${getNavShowcaseId('/ledger')}"]`],
    needsSidebar: true,
  },
  {
    moduleKey: 'keyboardMode',
    stepKey: 'keyboardMode.profile',
    route: '__start__',
    title: 'Keyboard mode access',
    description: 'Your role includes keyboard mode. Open it from the profile menu when you want faster navigation.',
    selectors: ['[data-showcase-id="topbar-account"]'],
  },
]);

function canUseStep(step, permissions = []) {
  if (!hasAnyPermission(permissions, step.permissionsAny || [])) {
    return false;
  }

  if (!hasEveryPermission(permissions, step.permissionsAll || [])) {
    return false;
  }

  return true;
}

export function getPreferredShowcaseStartPath(role, permissions, { enableAuth = true, authResolved = true } = {}) {
  if (
    canAccessPath('/dashboard', role, {
      enableAuth,
      permissions,
      authResolved,
    })
  ) {
    return '/dashboard';
  }

  const firstAccessibleRoute = standardRouteDefs.find((route) => {
    if (route.sessionScope || route.path.includes('/:')) {
      return false;
    }

    return canAccessPath(route.path, role, {
      enableAuth,
      permissions,
      authResolved,
    });
  });

  return firstAccessibleRoute?.path || '/dashboard';
}

export function buildWorkspaceShowcaseSteps({
  role,
  permissions = [],
  enableAuth = true,
  authResolved = true,
  startPath = '/dashboard',
}) {
  return SHOWCASE_STEPS.map((step) => ({
    ...step,
    route: step.route === '__start__' ? startPath : step.route,
  })).filter((step) => {
    if (!canUseStep(step, permissions)) {
      return false;
    }

    if (!step.route) {
      return true;
    }

    return canAccessPath(step.route, role, {
      enableAuth,
      permissions,
      authResolved,
    });
  });
}

export function resolveWorkspaceShowcaseTarget(step) {
  if (!step) {
    return null;
  }

  const selectors = Array.isArray(step.selectors) ? step.selectors : [];
  for (const selector of selectors) {
    if (!selector) {
      continue;
    }

    const match = document.querySelector(selector);
    if (match) {
      return match;
    }
  }

  return null;
}
